import { useState } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import type { PanelCard } from "./AddCardsPanel";

/* Cards shown per slide (one row on desktop). */
const PER_PAGE = 5;

/* A bottom "explore cards" carousel — compact, uniform tiles for each card the
   user hasn't added yet. Five per row at equal size; if there are more, prev/next
   arrows slide to the next set. "View details" opens the Manage-dashboard panel
   with that card spotlighted. */
export function CardLibraryCarousel({
  title = "Explore cards",
  subtitle,
  cards,
  onViewDetail,
}: {
  title?: string;
  subtitle?: string;
  cards: PanelCard[];
  onViewDetail: (id: string) => void;
}) {
  const [page, setPage] = useState(0);

  const pages: PanelCard[][] = [];
  for (let i = 0; i < cards.length; i += PER_PAGE) pages.push(cards.slice(i, i + PER_PAGE));
  const totalPages = Math.max(1, pages.length);
  const current = Math.min(page, totalPages - 1);

  if (cards.length === 0) return null;

  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      {/* header */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={current === 0}
              title="Previous"
              className="flex size-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:border-indigo-400/50 hover:text-foreground disabled:opacity-30"
            >
              <ChevronLeft className="size-4" />
            </button>
            <span className="text-xs font-semibold tabular-nums text-muted-foreground">
              {current + 1}/{totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={current === totalPages - 1}
              title="Next"
              className="flex size-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:border-indigo-400/50 hover:text-foreground disabled:opacity-30"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
        )}
      </div>

      {/* sliding track */}
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {pages.map((pageCards, i) => (
            <div
              key={i}
              className="grid w-full shrink-0 grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5"
            >
              {pageCards.map((card) => (
                <LibraryCard key={card.id} card={card} onViewDetail={onViewDetail} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LibraryCard({
  card, onViewDetail,
}: {
  card: PanelCard;
  onViewDetail: (id: string) => void;
}) {
  const Icon = card.icon;
  return (
    <div className="group flex h-full flex-col rounded-xl border border-border bg-background transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-400/60 hover:shadow-lg hover:shadow-indigo-500/10">
      <div className="flex flex-1 flex-col p-3.5">
        {/* top row: icon + availability badge — fixed position across all cards */}
        <div className="mb-2.5 flex items-center justify-between gap-2">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-foreground/[0.06]">
            <Icon className="size-4 text-foreground/70" />
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[9px] font-bold tracking-wider text-emerald-600 dark:text-emerald-400">
            <span className="size-1.5 rounded-full bg-emerald-500" />
            Available
          </span>
        </div>

        {/* category eyebrow — plain label, no box, consistent height */}
        <span className="mb-1 block text-[10px] font-semibold tracking-[0.12em] text-indigo-500/80 dark:text-indigo-400/80">
          {card.category}
        </span>

        {/* title — reserve two lines so every card's description lines up */}
        <h4 className="line-clamp-2 min-h-[2.5rem] text-sm font-bold leading-tight text-foreground">
          {card.title}
        </h4>

        {/* short description — clamped so the footer stays aligned */}
        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
          {card.description}
        </p>
      </div>

      {/* view-details action */}
      <div className="p-3 pt-0">
        <button
          type="button"
          onClick={() => onViewDetail(card.id)}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-indigo-500/40 bg-indigo-500/10 py-2 text-xs font-semibold text-indigo-600 transition-colors hover:border-indigo-600 hover:bg-indigo-600 hover:text-white dark:text-indigo-400"
        >
          View details
          <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
}
